import { findEntity, type PlayerIndex, type WorldState } from "../../sim";
import { wrappedDistance } from "../../sim/math/torus";
import { type Listener, panFor } from "./sound-policy";

/** Engine loop volume of a burning engine right next to a viewer (master volume applies on top). */
export const ENGINE_VOLUME = 0.3;
/** Engines are at full volume up to this distance from the nearest viewer's ship… */
const ENGINE_NEAR = 300;
/** …and fade out linearly until this one. */
const ENGINE_FAR = 1400;
/** Time constant (ms) of the volume ramp: no clicks when an engine starts or stops. */
const ENGINE_RAMP_MS = 50;

export interface EngineCue {
	player: PlayerIndex;
	/** 0 = silent. */
	volume: number;
	pan: number;
}

/**
 * Engine sound per player (P11.9): loud while their engine burns, fading with the distance to
 * the nearest viewer's ship, panned like the other sounds. Without a viewer ship (all destroyed,
 * or a spectator) engines play at full volume.
 */
export function engineMix(world: WorldState, listeners: readonly Listener[]): EngineCue[] {
	const ears = listeners.flatMap((l) => {
		const id = world.players[l.player]?.shipId;
		const ship = id == null ? undefined : findEntity(world, id);
		return ship ? [ship.pos] : [];
	});
	return world.players.map((p) => {
		const ship = p.shipId === null ? undefined : findEntity(world, p.shipId);
		if (ship?.kind !== "ship" || !ship.thrusting) return { player: p.index, volume: 0, pan: 0 };
		const near = ears.length
			? Math.min(...ears.map((pos) => wrappedDistance(pos, ship.pos, world.size)))
			: 0;
		const falloff = Math.max(0, Math.min(1, (ENGINE_FAR - near) / (ENGINE_FAR - ENGINE_NEAR)));
		return {
			player: p.index,
			volume: ENGINE_VOLUME * falloff,
			pan: panFor(ship.pos, world, listeners),
		};
	});
}

/** The looping sound surface the mixer needs (Phaser sounds satisfy it). */
export interface EngineVoice {
	readonly isPlaying: boolean;
	play(config?: { loop?: boolean; volume?: number }): boolean;
	stop(): boolean;
	setVolume(value: number): unknown;
	setPan?(value: number): unknown;
}

/** One looping engine voice per player, ramped towards the mix every frame. */
export class EngineMixer {
	private readonly voices: Array<{ voice: EngineVoice; level: number } | null> = [];

	constructor(private readonly create: () => EngineVoice | null) {}

	update(cues: readonly EngineCue[], deltaMs: number): void {
		const k = Math.min(1, deltaMs / ENGINE_RAMP_MS);
		for (const cue of cues) {
			let slot = this.voices[cue.player];
			if (slot === undefined) {
				const voice = this.create();
				slot = voice ? { voice, level: 0 } : null;
				this.voices[cue.player] = slot;
			}
			if (!slot) continue;
			slot.level += (cue.volume - slot.level) * k;
			if (cue.volume === 0 && slot.level < 0.005) {
				slot.level = 0;
				if (slot.voice.isPlaying) slot.voice.stop();
				continue;
			}
			if (!slot.voice.isPlaying) slot.voice.play({ loop: true, volume: slot.level });
			slot.voice.setVolume(slot.level);
			slot.voice.setPan?.(cue.pan);
		}
	}

	/** Paused or finished: every engine stops at once. */
	silence(): void {
		for (const slot of this.voices) {
			if (!slot) continue;
			slot.level = 0;
			if (slot.voice.isPlaying) slot.voice.stop();
		}
	}
}
